/**
 * Shared CacheStorage layer for HLS video segments and manifests.
 *
 * Both the Shaka Player networking scheme handler (shakaLoader.ts) and the
 * prefetch logic (useVideoUrls.ts) share this cache so that prefetched
 * responses are reused when the player is created, avoiding duplicate downloads.
 */

export const VIDEO_CACHE_NAME = 'bifrost-video-segments';
export const VIDEO_CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

export async function getVideoCache(): Promise<Cache | null> {
	if (!('caches' in globalThis)) return null;
	return caches.open(VIDEO_CACHE_NAME);
}

export async function getCachedResponse(url: string): Promise<Response | null> {
	const cache = await getVideoCache();
	if (!cache) return null;

	const cached = await cache.match(url);
	if (!cached) return null;

	const cachedAt = cached.headers.get('x-cached-at');
	if (cachedAt && Date.now() - Number(cachedAt) > VIDEO_CACHE_MAX_AGE_MS) {
		cache.delete(url).catch(() => {});
		return null;
	}

	return cached;
}

export async function cacheResponse(
	url: string,
	response: Response
): Promise<void> {
	const cache = await getVideoCache();
	if (!cache) return;

	const headers = new Headers(response.headers);
	headers.set('x-cached-at', String(Date.now()));

	const body = await response.clone().arrayBuffer();
	const cachedResponse = new Response(body, {
		status: response.status,
		statusText: response.statusText,
		headers
	});

	await cache.put(url, cachedResponse);
}

export async function clearVideoCache(): Promise<void> {
	if (!('caches' in globalThis)) return;
	await caches.delete(VIDEO_CACHE_NAME);
}
